import React, { useEffect, useRef, useState } from "react";
import { Box, Dialog, IconButton, TextField, Typography, Alert, CircularProgress, Tooltip, Badge, Fab } from "@mui/material";
import {
  Close as CloseIcon,
  Send as SendIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import PhotoEditor from "./PhotoEditor";

export const MAX_PHOTOS = 30; // WhatsApp's own limit per send (backend enforces the same)
const FONT = "inherit";

let itemSeq = 0;
const toItem = (file) => ({ id: `p${++itemSeq}`, file, url: URL.createObjectURL(file), caption: "" });

// WhatsApp-style "send photos" screen: the picked photos with a caption each, a thumbnail strip
// to switch / add / remove, and the photo editor. `onSend(files, captions)` resolves to
// { success, sent, message } — on a part-way failure the photos already sent are dropped, so
// pressing Send again only sends the rest.
const MediaComposer = ({ files, header = null, onCancel, onSend, t }) => {
  const [items, setItems] = useState([]);
  const [current, setCurrent] = useState(0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // item being edited
  const addInputRef = useRef(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => {
    if (!files) return undefined;
    setItems(files.slice(0, MAX_PHOTOS).map(toItem));
    setCurrent(0);
    setError(files.length > MAX_PHOTOS ? t("whatsappChat.maxPhotos", { max: MAX_PHOTOS }) : "");
    return () => itemsRef.current.forEach((it) => URL.revokeObjectURL(it.url));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const item = items[current];
  const setCaption = (v) => setItems((list) => list.map((it, i) => (i === current ? { ...it, caption: v } : it)));

  const remove = (index) => {
    const it = items[index];
    if (!it) return;
    URL.revokeObjectURL(it.url);
    const next = items.filter((_, i) => i !== index);
    if (!next.length) {
      onCancel();
      return;
    }
    setItems(next);
    setCurrent((c) => Math.min(index < c ? c - 1 : c, next.length - 1));
  };

  const addMore = (e) => {
    const picked = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    e.target.value = "";
    if (!picked.length) return;
    const room = MAX_PHOTOS - items.length;
    if (picked.length > room) setError(t("whatsappChat.maxPhotos", { max: MAX_PHOTOS }));
    const added = picked.slice(0, Math.max(0, room)).map(toItem);
    if (!added.length) return;
    setItems((list) => [...list, ...added]);
    setCurrent(items.length);
  };

  const applyEdit = (file) => {
    const target = editing;
    setEditing(null);
    setItems((list) =>
      list.map((it) => {
        if (it.id !== target.id) return it;
        URL.revokeObjectURL(it.url);
        return { ...it, file, url: URL.createObjectURL(file) };
      })
    );
  };

  const send = async () => {
    if (!items.length || sending) return;
    setSending(true);
    setError("");
    const result = await onSend(
      items.map((it) => it.file),
      items.map((it) => it.caption.trim())
    );
    setSending(false);
    if (result?.success) return; // parent closes the composer
    const sent = result?.sent || 0;
    if (sent > 0) {
      items.slice(0, sent).forEach((it) => URL.revokeObjectURL(it.url));
      setItems((list) => list.slice(sent));
      setCurrent(0);
      setError(`${t("whatsappChat.sendPartial", { sent, total: items.length })} ${result?.message || ""}`.trim());
    } else {
      setError(result?.message || t("whatsappChat.sendFailed"));
    }
  };

  return (
    <Dialog
      open={!!files}
      onClose={sending ? undefined : onCancel}
      fullScreen
      PaperProps={{ sx: { bgcolor: "#0c0a14", color: "#fff", display: "flex", flexDirection: "column" } }}
    >
      {/* Top bar */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1 }}>
        <IconButton onClick={onCancel} disabled={sending} aria-label={t("common.cancel")} sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
        <Typography sx={{ fontFamily: FONT, fontWeight: 600, flex: 1 }} noWrap>
          {items.length > 1 ? t("whatsappChat.photosCount", { count: items.length }) : item?.file?.name}
        </Typography>
        <Tooltip title={t("whatsappChat.editPhoto")}>
          <span>
            <IconButton onClick={() => setEditing(item)} disabled={!item || sending} aria-label={t("whatsappChat.editPhoto")} sx={{ color: "#fff" }}>
              <EditIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t("whatsappChat.removePhoto")}>
          <span>
            <IconButton onClick={() => remove(current)} disabled={!item || sending} aria-label={t("whatsappChat.removePhoto")} sx={{ color: "#fff" }}>
              <DeleteIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {header}

      {/* Current photo */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", px: 2 }}>
        {item && (
          <Box
            component="img"
            src={item.url}
            alt=""
            onDoubleClick={() => !sending && setEditing(item)}
            sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 1 }}
          />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
          {error}
        </Alert>
      )}

      {/* Caption */}
      <Box sx={{ px: 2, pt: 1.5, display: "flex", justifyContent: "center" }}>
        <TextField
          fullWidth
          size="small"
          color="secondary"
          multiline
          maxRows={4}
          placeholder={t("whatsappChat.addCaption")}
          value={item?.caption || ""}
          onChange={(e) => setCaption(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          disabled={sending}
          sx={{
            maxWidth: 720,
            "& .MuiOutlinedInput-root": { bgcolor: "rgba(255,255,255,.1)", color: "#fff", borderRadius: 2 },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,.2)" },
          }}
        />
      </Box>

      {/* Thumbnails + send */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", gap: 1, overflowX: "auto", py: 0.5 }}>
          {items.map((it, i) => (
            <Box
              key={it.id}
              component="img"
              src={it.url}
              alt=""
              onClick={() => setCurrent(i)}
              sx={{
                width: 56,
                height: 56,
                flexShrink: 0,
                objectFit: "cover",
                borderRadius: 1,
                cursor: "pointer",
                outline: i === current ? "3px solid #fff" : "1px solid rgba(255,255,255,.25)",
                outlineOffset: i === current ? -3 : -1,
              }}
            />
          ))}
          {items.length < MAX_PHOTOS && (
            <Tooltip title={t("whatsappChat.addPhotos")}>
              <Box
                onClick={() => !sending && addInputRef.current?.click()}
                sx={{
                  width: 56,
                  height: 56,
                  flexShrink: 0,
                  borderRadius: 1,
                  border: "1px dashed rgba(255,255,255,.4)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <AddIcon />
              </Box>
            </Tooltip>
          )}
          <input ref={addInputRef} type="file" hidden multiple accept="image/*" onChange={addMore} />
        </Box>
        <Badge
          badgeContent={items.length > 1 ? items.length : 0}
          color="secondary"
          overlap="circular"
          // Fab has a high z-index of its own — keep the count on top of it.
          sx={{ "& .MuiBadge-badge": { zIndex: (th) => th.zIndex.fab + 1, border: "2px solid #0c0a14" } }}
        >
          <Fab color="secondary" onClick={send} disabled={!items.length || sending} aria-label={t("whatsappChat.send")}>
            {sending ? <CircularProgress size={22} color="inherit" /> : <SendIcon />}
          </Fab>
        </Badge>
      </Box>

      {editing && <PhotoEditor file={editing.file} onCancel={() => setEditing(null)} onDone={applyEdit} t={t} />}
    </Dialog>
  );
};

export default MediaComposer;
